import {
  ArrowsRightLeftIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { useDebounce } from '@superfunnel/react-hooks/useDebounce';
import {
  AdsAccountKeyword,
  Constraints,
  CustomKeyword,
  KeywordConstraint,
} from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Button from '@superfunnel/ui-core/v2/Button';
import { httpsCallable } from 'firebase/functions';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { functions } from '../../Firebase';
import { distinct } from '../../utils';
import { SearchField } from '../SearchField';

interface Props {
  constraints: Constraints;
  open: boolean;
  onClose: () => void;
  onSave: (constraints: Constraints) => void;
}

export const KeywordsConstraintSelectorModal = ({
  constraints,
  open,
  onClose,
  onSave,
}: Props) => {
  const { selectedAccount } = useContext(UserContext);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query);
  const [isLoading, setIsLoading] = useState(false);
  const [keywords, setKeywords] = useState<AdsAccountKeyword[]>();

  const [selectedKeywords, setSelectedKeywords] = useState<KeywordConstraint[]>(
    [],
  );

  const addKeyword = (keywordToAdd: KeywordConstraint) => {
    // Support adding multiple keywords at once, split by @~@
    if (keywordToAdd.keyword.includes('@~@')) {
      const newUniqueKeywordsArray = keywordToAdd.keyword
        .split('@~@')
        .filter(Boolean)
        .map((keyword) => ({
          ...keywordToAdd,
          keyword,
          id: keywordToAdd.provider + '-' + keyword, // Generate unique ID for each split keyword
        }));
      setSelectedKeywords(
        distinct(
          [...selectedKeywords, ...newUniqueKeywordsArray],
          (a) => a.keyword,
        ),
      );
    } else {
      setSelectedKeywords(
        distinct([keywordToAdd, ...selectedKeywords], (a) => a.keyword),
      );
    }
  };

  const filters = useMemo(
    () => {
      return;
      // if (!selectedDynamicPage) return;

      // const connections = selectedDynamicPage.data().connections;
      // if (!connections || Object.keys(connections)) return;

      // return {
      //   $or: Object.values(connections).map((c) => {
      //     switch (c.type) {
      //       case 'adsAccount':
      //         return {
      //           adsAccountId: c.id,
      //           provider: c.provider,
      //         };

      //       case 'adsCampaign':
      //         return {
      //           campaignId: c.id,
      //           adsAccountId: c.adsAccountId,
      //           provider: c.provider,
      //         };

      //       case 'adsAdGroup':
      //         return {
      //           adGroupId: c.id,
      //           adsAccountId: c.adsAccountId,
      //           provider: c.provider,
      //         };

      //       case 'adsAd':
      //         return {
      //           id: c.id,
      //           adGroupId: c.adGroupId,
      //           adsAccountId: c.adsAccountId,
      //           provider: c.provider,
      //         };
      //     }
      //   }),
      // };
    },
    [
      /* selectedDynamicPage */
    ],
  );

  useEffect(() => {
    if (!selectedAccount || !open) {
      setKeywords(undefined);
      return;
    }

    const fn = httpsCallable<any, AdsAccountKeyword[]>(
      functions,
      'accounts/adsAccounts/getAdsAccountsKeywords',
    );

    setIsLoading(true);
    let relevant = true;
    fn({
      accountId: selectedAccount.id,
      provider: 'google',
      query: debouncedQuery,
      filter: filters,
      limit: 1000,
      skip: 0,
    })
      .then((res) => {
        if (!relevant) return;
        const allKeywords: AdsAccountKeyword[] = res.data?.map((d: any) => ({
          ...d,
          id: d._id,
        }));
        const freeTextKeyword: CustomKeyword | undefined =
          debouncedQuery &&
          allKeywords.every((k) => k.keyword !== debouncedQuery)
            ? {
                keyword: debouncedQuery,
                id: 'free-text-' + debouncedQuery,
                provider: 'custom',
                adsAccountId: 'custom',
              }
            : undefined;

        setKeywords(
          freeTextKeyword
            ? [freeTextKeyword, ...(allKeywords || [])]
            : allKeywords,
        );
      })
      .catch((e) => {
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      relevant = false;
    };
  }, [filters, debouncedQuery, selectedAccount, open]);

  useEffect(() => {
    setSelectedKeywords(constraints['keywords'] || []);
  }, [constraints]);

  const nonSelectedKeywords = useMemo(() => {
    if (!keywords || !selectedKeywords.length) return keywords;

    const keywordsSet = new Set(selectedKeywords.map((k) => k.keyword));
    return keywords.filter((k) => !keywordsSet.has(k.keyword));
  }, [keywords, selectedKeywords]);

  const actions = useMemo<ModalActionData[]>(
    () => [
      {
        type: 'secondary',
        title: 'Cancel',
        onClick: onClose,
      },
      {
        type: 'primary',
        title: 'Save',
        onClick: () => {
          onSave({
            ...constraints,
            keywords: selectedKeywords,
          });
          onClose();
        },
      },
    ],
    [constraints, onClose, onSave, selectedKeywords],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="5xl"
      actions={actions}
      actionsAlignment="end"
      title="Select Keywords"
    >
      <SearchField
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
      />

      <div
        className="flex max-h-full min-h-96 flex-row gap-4 pt-3"
        style={{ height: '50vh' }}
      >
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-end justify-between">
            <h3 className="text-sm font-medium">Keywords</h3>

            <Button
              variant="ghost"
              color="primary"
              size="sm"
              className="-my-1"
              disabled={!nonSelectedKeywords?.length || isLoading}
              onClick={() => {
                setSelectedKeywords(
                  distinct(
                    [
                      ...(nonSelectedKeywords?.map((k) => ({
                        keyword: k.keyword,
                        id: k.id,
                        provider: k.provider,
                      })) ?? []),
                      ...selectedKeywords,
                    ],
                    (a) => a.keyword,
                  ),
                );
              }}
            >
              Add All
            </Button>
          </div>
          <div className="flex-1 overflow-scroll rounded-lg border border-gray-200">
            {isLoading && (
              <div className="flex h-full items-center justify-center">
                <Loader />
              </div>
            )}
            {!isLoading && !nonSelectedKeywords?.length && (
              <div className="flex h-full items-center justify-center text-gray-400">
                No keywords found
                {debouncedQuery &&
                  ' for the search term "' + debouncedQuery + '"'}
              </div>
            )}
            {!isLoading && !!nonSelectedKeywords?.length && (
              <ul role="list" className="divide-y divide-gray-100">
                {nonSelectedKeywords?.map((item) => (
                  <Item
                    key={item.id}
                    item={item}
                    added={false}
                    onClick={() => {
                      addKeyword({
                        keyword: item.keyword,
                        id: item.id,
                        provider: item.provider,
                      });
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex h-full min-h-full shrink-0 items-center">
          <div>
            <ArrowsRightLeftIcon className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-end justify-between">
            <h3 className="text-sm font-medium">Selected Keywords</h3>

            <Button
              variant="ghost"
              color="negative"
              size="sm"
              className="-my-1"
              disabled={!selectedKeywords.length}
              onClick={() => {
                setSelectedKeywords([]);
              }}
            >
              Remove All
            </Button>
          </div>
          <div className="flex-1 overflow-scroll rounded-lg border border-gray-200">
            {!selectedKeywords.length && (
              <div className="text flex h-full items-center justify-center text-gray-400">
                No keywords selected
              </div>
            )}
            <ul role="list" className="divide-y divide-gray-100">
              {selectedKeywords.map((item) => (
                <Item
                  key={item.id}
                  item={item}
                  added
                  onClick={() => {
                    setSelectedKeywords(
                      selectedKeywords.filter(
                        (k) => k.keyword !== item.keyword,
                      ),
                    );
                  }}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface ItemProps {
  item: KeywordConstraint | AdsAccountKeyword;
  onClick: () => void;
  added: boolean;
}
const Item = ({ item, onClick, added }: ItemProps) => {
  const name = item.keyword;

  return (
    <li
      className="flex cursor-pointer items-center justify-between gap-x-6 px-3 py-3 hover:bg-gray-50"
      onClick={onClick}
    >
      <div className="min-w-0 flex-auto">
        <p className="text-sm/6 font-semibold text-gray-900">{name}</p>
      </div>

      {!added ? (
        <PlusIcon className="size-4 text-gray-600" />
      ) : (
        <TrashIcon className="size-4 text-red-500" />
      )}
    </li>
  );
};
